import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { Search, ArrowUpDown } from "lucide-react";
import { PageHeader, Loading, Empty } from "@/components/Common";
import { EUR, EURc, num, pnlColor, fmtDate, dur } from "@/lib/format";
import api from "@/lib/api";

function Sel({ value, onChange, options, placeholder, testId }) {
  return (
    <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testId}
      className="rounded-lg border border-white/10 bg-[#0F0F12] px-3 py-2 font-sans text-xs text-zinc-300 outline-none focus:border-gold/40">
      <option value="">{placeholder}</option>
      {options.map((o) => <option key={o.v ?? o} value={o.v ?? o}>{o.l ?? o}</option>)}
    </select>
  );
}

export default function Trades() {
  const navigate = useNavigate();
  const [f, setF] = useState({ search: "", symbol: "", direction: "", result: "", session: "", setup: "" });
  const [sort, setSort] = useState({ key: "close_time", order: "desc" });

  const qs = new URLSearchParams(
    Object.entries({ ...f, sort: sort.key, order: sort.order }).filter(([, v]) => v)
  ).toString();

  const { data, isLoading } = useQuery({
    queryKey: ["trades", qs],
    queryFn: () => api.get(`/trades?${qs}`).then((r) => r.data),
  });

  const set = (k) => (v) => setF((s) => ({ ...s, [k]: v }));
  const toggleSort = (key) => setSort((s) => ({ key, order: s.key === key && s.order === "desc" ? "asc" : "desc" }));

  const filters = data?.filters || { symbols: [], setups: [], sessions: [] };
  const trades = data?.trades || [];

  return (
    <div data-testid="trades-page">
      <PageHeader title="Trades" subtitle={`${data?.count ?? 0} operazioni`} />

      <div className="mb-5 flex flex-wrap items-center gap-2">
        <div className="flex min-w-[180px] flex-1 items-center gap-2 rounded-lg border border-white/10 bg-[#0F0F12] px-3">
          <Search size={14} className="text-zinc-500" />
          <input value={f.search} onChange={(e) => set("search")(e.target.value)} placeholder="Cerca ticket o simbolo…"
            data-testid="trades-filter-input"
            className="w-full bg-transparent py-2 font-sans text-xs text-white outline-none placeholder:text-zinc-600" />
        </div>
        <Sel value={f.symbol} onChange={set("symbol")} options={filters.symbols} placeholder="Simbolo" testId="trades-filter-symbol" />
        <Sel value={f.direction} onChange={set("direction")} options={[{ v: "BUY", l: "BUY" }, { v: "SELL", l: "SELL" }]} placeholder="Direzione" testId="trades-filter-direction" />
        <Sel value={f.result} onChange={set("result")} options={[{ v: "profit", l: "Profit" }, { v: "loss", l: "Loss" }]} placeholder="Risultato" testId="trades-filter-result" />
        <Sel value={f.session} onChange={set("session")} options={filters.sessions} placeholder="Sessione" testId="trades-filter-session" />
        <Sel value={f.setup} onChange={set("setup")} options={filters.setups} placeholder="Setup" testId="trades-filter-setup" />
      </div>

      {isLoading ? <Loading /> : trades.length === 0 ? <Empty label="Nessun trade trovato con questi filtri" /> : (
        <div className="dlx-card overflow-x-auto" data-testid="trades-table">
          <table className="w-full min-w-[900px]">
            <thead>
              <tr className="border-b border-white/8 text-left">
                {[
                  { k: "close_time", l: "Data" }, { k: "symbol", l: "Simbolo" }, { k: "direction", l: "Dir" },
                  { k: "volume", l: "Vol" }, { k: "entry_price", l: "Entry" }, { k: "exit_price", l: "Exit" },
                  { k: "net_profit", l: "Net P&L" }, { k: "r_multiple", l: "R" }, { k: "duration_seconds", l: "Durata" },
                  { k: "setup", l: "Setup" }, { k: "session", l: "Sessione" },
                ].map((h) => (
                  <th key={h.k} onClick={() => toggleSort(h.k)}
                    className="cursor-pointer select-none px-4 py-3.5 font-sans text-[10px] font-medium uppercase tracking-widest text-zinc-500 hover:text-gold">
                    <span className="inline-flex items-center gap-1">{h.l}<ArrowUpDown size={10} /></span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {trades.map((t) => (
                <tr key={t.id} onClick={() => navigate(`/trades/${t.id}`)} data-testid={`trade-row-${t.ticket}`}
                  className="cursor-pointer border-b border-white/5 transition-colors hover:bg-gold/[0.03]">
                  <td className="px-4 py-3 font-mono text-xs text-zinc-400">{fmtDate(t.close_time)}</td>
                  <td className="px-4 py-3 font-sans text-sm font-medium text-white">{t.symbol}</td>
                  <td className="px-4 py-3">
                    <span className="font-mono text-[10px] font-semibold" style={{ color: t.direction === "BUY" ? "#10B981" : "#EF4444" }}>{t.direction}</span>
                  </td>
                  <td className="px-4 py-3 font-mono text-xs text-zinc-400">{t.volume}</td>
                  <td className="px-4 py-3 font-mono text-xs text-zinc-400">{num(t.entry_price, 2)}</td>
                  <td className="px-4 py-3 font-mono text-xs text-zinc-400">{num(t.exit_price, 2)}</td>
                  <td className={`px-4 py-3 font-mono text-sm font-semibold ${pnlColor(t.net_profit)}`}>{EURc(t.net_profit)}</td>
                  <td className={`px-4 py-3 font-mono text-xs ${pnlColor(t.r_multiple)}`}>{t.r_multiple?.toFixed(2)}</td>
                  <td className="px-4 py-3 font-mono text-xs text-zinc-500">{dur(t.duration_seconds)}</td>
                  <td className="px-4 py-3"><span className="rounded-full border border-gold/20 bg-gold/5 px-2 py-0.5 font-sans text-[10px] text-gold">{t.setup}</span></td>
                  <td className="px-4 py-3 font-sans text-xs text-zinc-400">{t.session}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
